/**
 * The app's single entry point to @lulalife/design-system.
 *
 * Import DS components from `src/components/DS` — never from
 * `@lulalife/design-system` directly. Routing every call site through this
 * barrel is the swap seam: when a DS prop shape changes, or a component has to
 * be shimmed while the upstream gap is filled, it gets fixed here once instead
 * of at every usage.
 *
 * Only the primitives the converted settings surfaces actually use are
 * re-exported. Add to this list as conversions need more — deliberately, so the
 * surface stays reviewable.
 *
 * Not available upstream as of 0.13.0, so do not go looking: Toast, Pagination,
 * Accordion, Drawer, TimePicker. (Card shipped after 0.3.1 and is now present
 * upstream — still not re-exported here because no surface needs it yet, not
 * because it is missing.)
 *
 * Popover: DS `Menu` (with `variant="popover"` on the root, 0.14.0) is
 * an arbitrary-content, trigger-anchored popover — used by the Skill Set "Copy
 * skills" panel. It and its `MenuContent` / `MenuTrigger` are
 * imported straight from `@lulalife/design-system` at that call site, not
 * re-exported here — nothing shims them. (Before 0.24.0 they were named
 * `DropdownMenu*`, which collided with reactstrap's `DropdownMenu` that every
 * unconverted surface still uses.)
 *
 * 0.3.1 DID add Table (plus TableToolbar, TableOptionsMenu, Filters) and
 * FileUpload. None is re-exported here — nothing in this app composes them, and
 * at 0.21.1 `TableOptionsMenu` has left the package barrel. The `Table`
 * primitives reach the app through `src/components/DS/ListingTable` instead.
 *
 * ---------------------------------------------------------------------------
 * TAILWIND CLASSES BELONG IN THIS DIRECTORY, NOWHERE ELSE.
 * ---------------------------------------------------------------------------
 * `src/app/design-system.css` pins Tailwind's source scan to the DS package and
 * `src/components/DS/**`. A utility class written anywhere else — a view, a
 * shared component — is simply never generated, so it silently does nothing.
 * Widening the scan would also generate new utilities whose names collide with
 * legacy app class names, which is what the guard block in
 * `design-system-compat.css` exists to contain. So: layout lives in the kit
 * components here, and call sites pass props.
 */

/**
 * 6.5 vendor link "Finish the job" page: the auth card is the DS's public,
 * logged-out card (logo, heading, content) — the same shape as the backend's
 * offer-link page — and FileUpload carries the before/after photo pickers.
 */
/** Table's group header control (My Tasks › To do's "Urgent · 3"). */
export {
  AuthAppShell,
  AuthCard,
  AuthCardContent,
  AuthCardHeader,
  AuthCardMessage,
  AuthCardSecondaryAction,
  AuthCardSso,
  FileUpload,
  FileUploadDropzone,
  FileUploadErrorText,
  type FileUploadItemData,
  FileUploadLabel,
  FileUploadList,
  type FileUploadRejection,
  KeyValue,
  ListRow,
  Menu,
  MenuContent,
  MenuSearch,
  MenuTrigger,
  NumberInput,
  type NumberInputProps,
  PasswordRequirement,
  PasswordRequirements,
  Section,
  Table,
  TableBody,
  TableCell,
  TableCellSkeleton,
  TableFooter,
  TableGroupBy,
  TableHead,
  TableHeader,
  TableRow,
} from '@lulalife/design-system';
export { ApplianceCard } from './ApplianceCard';
export { AuthLink } from './AuthLink';
export { AuthScreen, type AuthScreenProps } from './AuthScreen';
export { type AvailabilityDay, AvailabilityDays } from './AvailabilityDays';
export { ContactCard, type ContactCardBadge } from './ContactCard';
export { DateRangePresets } from './DateRangePresets';
export { DSSurface } from './DSSurface';
/**
 * `src/components/DS/FormFields/` mirrors `src/components/Shared/FormFields/`
 * one-for-one: same component name, same `name`-bound API, same hidden
 * `Controller`/`useController` wiring — only the rendered control changed, from
 * reactstrap/react-select to the design system. A tab converts a field by
 * swapping the import from `src/components/Shared/FormFields/<X>` to
 * `src/components/DS` and nothing else. `FormFields/Select.tsx` wraps the raw
 * DS `Select` directly — it takes `label` (visible) or `aria-label`
 * (settings-row, nothing drawn), both native on DS `Select` since 0.14.0.
 */
export { CheckBox } from './FormFields/CheckBox';
export { CodeInputField, type CodeInputFieldProps } from './FormFields/CodeInputField';
export { CustomNumberField } from './FormFields/CustomNumberField';
export { DatePicker } from './FormFields/DatePicker';
export { InputField, type InputFieldProps } from './FormFields/InputField';
export { Select, type SelectFieldProps } from './FormFields/Select';
export { Toggle } from './FormFields/Toggle';
export {
  type ListFilterChip,
  ListFilterChips,
  type ListFilterField,
  type ListFilterSelection,
  ListFiltersMenu,
  selectionChips,
} from './ListFilters';
export {
  type ListColumnOption,
  type ListGroupByOption,
  ListTableOptionsMenu,
} from './ListTableOptionsMenu';
export { ListToolbar } from './ListToolbar';
export { type ListViewOption, ListViewsMenu } from './ListViewsMenu';
export { ModalConfirmationDescription } from './ModalConfirmationDescription';
/** Local stand-in until the DS Popover (Figma, 10-15) reaches npm; see the file. */
export { Popover } from './Popover';
export {
  PROPERTY_LIST_COLUMN_WIDTHS,
  PropertyAddressCell,
  PropertyMarketCell,
} from './PropertyListCells';
export { PropertyLocationMap } from './PropertyLocationMapLazy';
export { PropertyTabError, PropertyTabState } from './PropertyTabState';
export { ResultCountFooter } from './ResultCountFooter';
/** More's Service Requests list (7431:38466): cells, the assignee picker and row actions. */
export {
  AssigneeAvatars,
  AssigneePickerMenu,
  ListRowActions,
  ListRowMorePlaceholder,
  ListRowOpenButton,
  ListTextCell,
  SERVICE_REQUEST_LIST_COLUMN_MIN_WIDTHS,
  SERVICE_REQUEST_LIST_COLUMN_WIDTHS,
} from './ServiceRequestListCells';
export { SettingsBreadcrumb } from './SettingsBreadcrumb';
export { SettingsChipGroup } from './SettingsChipGroup';
export { SettingsColorTag } from './SettingsColorTag';
export { SettingsContentPane } from './SettingsContentPane';
export { SettingsDeleteRoleModal } from './SettingsDeleteRoleModal';
export { SettingsDiscardModal } from './SettingsDiscardModal';
export { SettingsDisclosure } from './SettingsDisclosure';
export { SettingsField } from './SettingsField';
export { SettingsFieldError } from './SettingsFieldError';
export { SettingsFilterChip } from './SettingsFilterChip';
export { SettingsFootnote } from './SettingsFootnote';
export { SettingsInlineRow, SettingsInlineStack } from './SettingsInlineRow';
export { SettingsLink } from './SettingsLink';
export { SettingsNote } from './SettingsNote';
export { SettingsPage } from './SettingsPage';
export { SettingsPageHeader } from './SettingsPageHeader';
export { SettingsParamRow } from './SettingsParamRow';
export { SettingsReassignUsersModal } from './SettingsReassignUsersModal';
export { SettingsRow } from './SettingsRow';
export { SettingsRowGroup } from './SettingsRowGroup';
export { SAAS_1502_SETTING_ROW_LAYOUT } from './SettingsRowLayout';
export { SettingsSaveBar } from './SettingsSaveBar';
export { SettingsSection } from './SettingsSection';
/**
 * DS `Select` is imported straight from `@lulalife/design-system` at its call
 * sites — 0.14.0 gave it `id` / `aria-label` / `aria-labelledby`, so naming a
 * `role="combobox"` on a settings row no longer needs a wrapper. `SelectOption`
 * / `SelectProps` come from the package too.
 */
export { SettingsTabStrip, type SettingsTabStripItem } from './SettingsTabStrip';
export { SettingsToolbar } from './SettingsToolbar';
export { SettingsUserCell } from './SettingsUserCell';
export {
  SettingsWorkingHoursRow,
  SettingsWorkingHoursStack,
} from './SettingsWorkingHoursRow';
/** D3b: the slideout's tab rail and header. */
export {
  SlideoutPanelHeader,
  SlideoutPanelTitle,
  SlideoutParentLink,
  SlideoutRail,
  type SlideoutRailTab,
  SlideoutShell,
  SlideoutShellHeader,
} from './SlideoutChrome';
/** More's Slideout Overview (10-30): local copies of parts the DS hasn't published, and its states. */
export { SlideoutContentHeader } from './SlideoutContentHeader';
export {
  OverviewSections,
  SlideoutEmptyBlock,
  SlideoutLoadFailed,
  SlideoutOverviewSkeleton,
} from './SlideoutOverviewStates';
/** More's Work Orders slideout (7460:2364): the Trips, Quotes and Invoices tabs' panel, cards and empties. */
export {
  DSReasonButton,
  SlideoutRecordActions,
  SlideoutRecordCard,
  SlideoutRecordNote,
  SlideoutRecordRow,
  SlideoutRecordRows,
  SlideoutRecordStatus,
  SlideoutRecordTotal,
  SlideoutTabBody,
  SlideoutTabEmpty,
  SlideoutTabHeader,
  SlideoutTabPanel,
} from './SlideoutRecordParts';
// Design-system primitives, icons and shell skeletons live in `./shell` so startup code can
// import them without loading the local form-field and settings wrappers below.
export * from './shell';
/** More's Work Orders list (7453:980): column widths. */
export {
  ListNoWrapCell,
  WORK_ORDER_LIST_COLUMN_MIN_WIDTHS,
  WORK_ORDER_LIST_COLUMN_WIDTHS,
} from './WorkOrderListCells';
/** D3: the work order slideout's Overview body (field rows, status and priority tags, sections). */
export {
  DSDropdownToggleButton,
  OverviewMetaRow,
  OverviewMetaRows,
  OverviewPriorityTag,
  OverviewSection,
  OverviewStatusTag,
} from './WorkOrderOverview';
